/**
 * Year6MissionScreen — /year6/:slug/screen
 *
 * Full-screen projector view. Rotates 30 missions every 60s, shows:
 *  - current mission title + category
 *  - big countdown timer with panic pulse in last 10s
 *  - live QR so kids can scan and upload
 *  - team leaderboard sidebar (top 4 houses with live scores)
 *  - confetti burst when a mission rolls over
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkle, GraduationCap, Camera } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 1000;

// Categories where a Pocket Booth capture is the natural action (photo-leaning
// missions). reflection is text-introspective so we hide the booth there.
const BOOTH_CATEGORIES = new Set(['friendship', 'silly', 'school', 'last_time']);

export default function Year6MissionScreen() {
  const { slug } = useParams();
  const [eventId, setEventId] = useState(null);
  const [state, setState] = useState(null);
  const [teams, setTeams] = useState([]);
  const prevIndex = useRef(null);
  const [confettiKey, setConfettiKey] = useState(0);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (!cancelled) setEventId(r.data.event_id);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [s, t] = await Promise.all([
          axios.get(`${API}/events/${eventId}/year6/mission-state`),
          axios.get(`${API}/events/${eventId}/year6/teams`),
        ]);
        if (cancelled) return;
        setState(s.data);
        setTeams(t.data?.teams || []);
        // Confetti when mission rolls over
        const nextIdx = s.data?.mission?.index;
        if (nextIdx && prevIndex.current && nextIdx !== prevIndex.current) {
          setConfettiKey((k) => k + 1);
        }
        prevIndex.current = nextIdx;
      } catch { /* ignore */ }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [eventId]);

  const active = state?.active;
  const paused = state?.paused;
  const mission = state?.mission;
  const secRemain = state?.seconds_remaining ?? 60;
  const isPanic = active && !paused && secRemain <= 10;

  return (
    <div className="relative min-h-screen overflow-hidden bg-neutral-950 text-white" data-testid="year6-mission-screen">
      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 10%, rgba(59,130,246,0.25), transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(250,204,21,0.18), transparent 55%)' }} />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-8 py-6 border-b border-white/5">
        <div className="inline-flex items-center gap-2">
          <GraduationCap size={22} weight="fill" className="text-amber-300" />
          <span className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-200">Year 6 Mission Log</span>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/50">{state?.event_name || 'Class'}</p>
          <p className="text-[11px] text-white/30">{state?.school_name || ''}</p>
        </div>
      </div>

      {/* Main */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 px-8 py-10">
        {/* Mission + countdown */}
        <div className="flex flex-col justify-center min-h-[65vh]">
          {!active && (
            <div className="text-center" data-testid="year6-screen-waiting">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-white/40 mb-3">Waiting for teacher</p>
              <h1 className="font-serif text-5xl sm:text-7xl font-black leading-[1.02] mb-4">Ready when you are</h1>
              <p className="text-white/55 text-lg">Tap <strong>Start Missions</strong> on the teacher controls.</p>
            </div>
          )}
          {active && (
            <AnimatePresence mode="wait">
              <motion.div
                key={`m-${mission?.index || 0}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.45 }}
                data-testid="year6-screen-mission"
              >
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-300 mb-3">
                  Mission {String(mission?.index || 1).padStart(2, '0')} · {mission?.category || ''}
                </p>
                <h1 className="font-serif text-5xl sm:text-7xl font-black leading-[1.05] mb-8">
                  {mission?.title || '—'}
                </h1>
                <div className="flex items-end gap-4">
                  <motion.span
                    key={secRemain}
                    initial={{ scale: 0.92, opacity: 0.6 }}
                    animate={{ scale: isPanic ? [1, 1.1, 1] : 1, opacity: 1 }}
                    transition={{ duration: isPanic ? 0.5 : 0.3, repeat: isPanic ? Infinity : 0 }}
                    className={`font-mono font-black tabular-nums leading-none ${isPanic ? 'text-rose-400' : 'text-white'}`}
                    style={{ fontSize: 'clamp(80px, 16vw, 220px)' }}
                    data-testid="year6-screen-countdown"
                  >
                    {String(Math.max(0, secRemain)).padStart(2, '0')}
                  </motion.span>
                  <span className="pb-6 text-white/40 font-black uppercase tracking-widest">seconds</span>
                </div>
                {paused && (
                  <p className="mt-4 inline-block px-3 py-1 rounded-full bg-amber-300/15 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em]" data-testid="year6-screen-paused">
                    Paused
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Right rail: QR + team leaderboard */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-5 flex flex-col items-center" data-testid="year6-screen-qr">
            <QRCodeSVG value={shareUrl} size={240} bgColor="#FFFFFF" fgColor="#0F1014" includeMargin={false} />
            <p className="text-neutral-600 text-[11px] font-black uppercase tracking-[0.18em] mt-3">Scan to upload</p>
          </div>

          {/* Pocket Booth callout — aligns instruction (mission) + tool (booth) + access (QR).
              Shown only on photo-leaning categories (friendship/silly/school/last_time)
              so the mission and the action point at the same thing. Subtle pulse
              draws the eye without nagging. */}
          {mission?.category && BOOTH_CATEGORIES.has(mission.category) && (
            <div
              className="rounded-2xl p-[1.5px] animate-[boothPulse_3.5s_ease-in-out_infinite]"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #FACC15 100%)' }}
              data-testid="year6-screen-pocket-booth-cta"
            >
              <div className="rounded-2xl bg-[#0F1014] px-4 py-4 flex items-center gap-3">
                <div
                  className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #FACC15 100%)' }}
                >
                  <Camera size={24} weight="fill" className="text-black" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52] mb-0.5">Use the</p>
                  <p className="text-white text-lg font-black leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                    Pocket Booth
                  </p>
                  <p className="text-white/60 text-[11px] mt-0.5">Scan QR · take your photo instantly</p>
                </div>
              </div>
            </div>
          )}
          <style>{`@keyframes boothPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(236, 72, 153, 0); } 50% { box-shadow: 0 0 0 8px rgba(236, 72, 153, 0.18); } }`}</style>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="year6-screen-leaderboard">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/45 mb-3 inline-flex items-center gap-1.5">
              <Sparkle size={10} weight="fill" className="text-amber-300" /> House Leaderboard
            </p>
            <ul className="space-y-2">
              {teams.map((t, i) => (
                <li key={t.id} className="flex items-center gap-3" data-testid={`year6-team-row-${t.id}`}>
                  <span className="w-4 text-white/45 text-[12px] font-black text-right">{i + 1}</span>
                  <span className="w-3 h-3 rounded-full" style={{ background: t.color }} />
                  <span className="flex-1 text-sm font-bold">{t.name}</span>
                  <span className="text-sm font-mono tabular-nums text-white/80">{t.score}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Confetti burst (simple CSS-only) */}
      {confettiKey > 0 && (
        <div key={confettiKey} className="fixed inset-0 pointer-events-none z-30" data-testid="year6-screen-confetti">
          {Array.from({ length: 40 }).map((_, i) => {
            const left = Math.random() * 100;
            const delay = Math.random() * 0.5;
            const duration = 1.8 + Math.random() * 1.2;
            const color = ['#3B82F6', '#EF4444', '#22C55E', '#FACC15', '#8B5CF6'][i % 5];
            return (
              <span
                key={i}
                className="absolute top-0 w-2 h-3 rounded-sm"
                style={{
                  left: `${left}%`,
                  background: color,
                  animation: `y6fall ${duration}s ${delay}s ease-in forwards`,
                }}
              />
            );
          })}
        </div>
      )}
      <style>{`
        @keyframes y6fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}
